import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Award, ScrollText, FileCheck, ArrowRight, CheckCircle2, Loader2, Wallet,
} from "lucide-react";
import SiteHeader from "@/components/SiteHeader";
import SiteFooter from "@/components/SiteFooter";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { api } from "@/lib/api";

const RECORD_TYPES = [
  { id: "diploma", label: "Diploma", Icon: Award, feeKey: "diploma_fee",
    desc: "Official diploma for graduates of Zion Christian Academy." },
  { id: "report_card", label: "Report Card", Icon: ScrollText, feeKey: "report_card_fee",
    desc: "Official report card for any completed school year." },
  { id: "verification_letter", label: "Verification Letter", Icon: FileCheck, feeKey: "verification_letter_fee",
    desc: "Letter verifying a student's enrollment or graduation." },
];

export default function InstantRecordsPage() {
  const [settings, setSettings] = useState(null);
  const [recordType, setRecordType] = useState("verification_letter");
  const [form, setForm] = useState({
    student_full_name: "", student_dob: "", prior_grade: "", prior_year: "",
    buyer_first_name: "", buyer_last_name: "", buyer_email: "", buyer_phone: "",
    delivery_address: "", payment_method: "cash_check_now", notes: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [confirmation, setConfirmation] = useState(null);

  useEffect(() => {
    api.get("/settings").then((r) => setSettings(r.data)).catch(() => setSettings({}));
  }, []);

  const selectedType = RECORD_TYPES.find((r) => r.id === recordType);
  const fee = settings ? Number(settings[selectedType.feeKey] || 0) : 0;

  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const { data } = await api.post("/instant-records", { ...form, record_type: recordType });
      setConfirmation(data);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not submit request.");
    } finally {
      setSubmitting(false);
    }
  };

  if (confirmation) {
    return (
      <div className="min-h-screen bg-white">
        <SiteHeader />
        <section className="bg-purple-dark text-white">
          <div className="max-w-3xl mx-auto px-5 sm:px-8 py-16 text-center">
            <div className="inline-flex items-center justify-center h-14 w-14 rounded-full bg-gold text-ink mb-6">
              <CheckCircle2 size={26} strokeWidth={3} />
            </div>
            <div className="text-xs tracking-[0.22em] uppercase text-gold-light font-bold mb-3">Request received</div>
            <h1 className="h-serif text-4xl sm:text-5xl tracking-tight mb-4">
              Thank you for your request.
            </h1>
            <p className="text-white/85 leading-relaxed max-w-xl mx-auto">
              Reference ID{" "}
              <span className="font-mono bg-white/10 px-3 py-1 rounded-sm text-gold-light">
                {confirmation.request_id}
              </span>
              . A confirmation email is on its way to <b className="text-gold-light">{form.buyer_email}</b>.
            </p>
          </div>
        </section>
        <section className="max-w-3xl mx-auto px-5 sm:px-8 py-12">
          <div className="border border-grey p-6 sm:p-8">
            <div className="text-xs tracking-[0.15em] uppercase text-purple-light font-bold mb-3">
              What happens next
            </div>
            <ol className="space-y-3 text-sm text-ink/85 list-decimal ml-5">
              <li>Director Brady Renfroe will personally review your request.</li>
              <li>Submit your payment (${Number(confirmation.amount_due).toFixed(2)}) per the instructions in the confirmation email.</li>
              <li>Once payment is received, your {RECORD_TYPES.find(r => r.id === confirmation.record_type)?.label} will be emailed to you (and optionally mailed if you provided an address).</li>
            </ol>
            <Link
              to="/"
              className="inline-block mt-8 text-purple hover:text-purple-dark link-underline"
              data-testid="records-return-home"
            >
              ← Return home
            </Link>
          </div>
        </section>
        <SiteFooter />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <SiteHeader />
      <section className="bg-purple-dark text-white">
        <div className="max-w-4xl mx-auto px-5 sm:px-8 py-14">
          <div className="text-xs tracking-[0.22em] uppercase text-gold-light font-bold mb-3">
            Instant records request
          </div>
          <h1 className="h-serif text-4xl sm:text-5xl tracking-tight mb-4">
            Request a school record.
          </h1>
          <p className="text-white/85 leading-relaxed max-w-2xl">
            For former students, employers, and colleges: request an official
            Diploma, Report Card, or Verification Letter. Requests are reviewed
            and issued within 3&ndash;5 business days once payment is received.
          </p>
        </div>
      </section>

      <section className="max-w-4xl mx-auto px-5 sm:px-8 py-12">
        <form onSubmit={submit} className="space-y-8" data-testid="instant-records-form">
          <SectionCard title="1. What record do you need?">
            <div className="grid sm:grid-cols-3 gap-3">
              {RECORD_TYPES.map((t) => {
                const active = t.id === recordType;
                const fee = settings ? Number(settings[t.feeKey] || 0) : 0;
                return (
                  <button
                    type="button"
                    key={t.id}
                    onClick={() => setRecordType(t.id)}
                    data-testid={`record-type-${t.id}`}
                    className={`text-left border p-4 transition-colors ${
                      active ? "border-purple bg-grey-light" : "border-grey bg-white hover:border-purple-light"
                    }`}
                  >
                    <div className="flex items-center gap-3 mb-2">
                      <t.Icon className={active ? "text-purple" : "text-ink/60"} size={20} />
                      <div className="font-bold text-purple-dark">{t.label}</div>
                    </div>
                    <p className="text-xs text-ink/70 leading-relaxed mb-3">{t.desc}</p>
                    <div className="text-lg font-bold text-gold">${fee.toFixed(2)}</div>
                  </button>
                );
              })}
            </div>
          </SectionCard>

          <SectionCard title="2. Student information">
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Student's full legal name" required>
                <Input value={form.student_full_name} onChange={(e) => upd("student_full_name", e.target.value)} required data-testid="student-name" className="border-grey" />
              </Field>
              <Field label="Date of birth" required>
                <Input type="date" value={form.student_dob} onChange={(e) => upd("student_dob", e.target.value)} required data-testid="student-dob" className="border-grey" />
              </Field>
              <Field label="Grade / graduation status" required>
                <Input placeholder="e.g. 12th (graduated) or 5th grade" value={form.prior_grade} onChange={(e) => upd("prior_grade", e.target.value)} required data-testid="prior-grade" className="border-grey" />
              </Field>
              <Field label="School year" required>
                <Input placeholder="e.g. 2025-2026" value={form.prior_year} onChange={(e) => upd("prior_year", e.target.value)} required data-testid="prior-year" className="border-grey" />
              </Field>
            </div>
          </SectionCard>

          <SectionCard title="3. Who's requesting it?">
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="First name" required>
                <Input value={form.buyer_first_name} onChange={(e) => upd("buyer_first_name", e.target.value)} required data-testid="buyer-first" className="border-grey" />
              </Field>
              <Field label="Last name" required>
                <Input value={form.buyer_last_name} onChange={(e) => upd("buyer_last_name", e.target.value)} required data-testid="buyer-last" className="border-grey" />
              </Field>
              <Field label="Email" required>
                <Input type="email" value={form.buyer_email} onChange={(e) => upd("buyer_email", e.target.value)} required data-testid="buyer-email" className="border-grey" />
              </Field>
              <Field label="Phone" required>
                <Input value={form.buyer_phone} onChange={(e) => upd("buyer_phone", e.target.value)} required data-testid="buyer-phone" className="border-grey" />
              </Field>
              <div className="sm:col-span-2">
                <Field label="Mailing address (optional — if you need a physical copy mailed)">
                  <Textarea rows={2} value={form.delivery_address} onChange={(e) => upd("delivery_address", e.target.value)} data-testid="delivery-address" className="border-grey" />
                </Field>
              </div>
              <div className="sm:col-span-2">
                <Field label="Notes for the director (optional)">
                  <Textarea rows={2} value={form.notes} onChange={(e) => upd("notes", e.target.value)} data-testid="notes" className="border-grey" placeholder="Any context or special instructions." />
                </Field>
              </div>
            </div>
          </SectionCard>

          <SectionCard title="4. Payment">
            <RadioGroup value={form.payment_method} onValueChange={(v) => upd("payment_method", v)}>
              <PayOption id="cash_check_now" label="Cash or Check (paid now)" desc="Mail a check or contact us to arrange in-person drop-off." checked={form.payment_method === "cash_check_now"} />
              <PayOption id="payment_plan_end_of_year" label="Payment plan" desc="Contact the school to arrange a payment schedule." checked={form.payment_method === "payment_plan_end_of_year"} />
            </RadioGroup>
            <div className="mt-5 bg-grey-light border border-grey p-4 flex items-center justify-between">
              <div>
                <div className="text-[10px] tracking-widest uppercase text-purple-light font-bold">Total due</div>
                <div className="text-2xl h-serif text-purple-dark">${fee.toFixed(2)}</div>
              </div>
              <Wallet className="text-purple/40" size={30} />
            </div>
          </SectionCard>

          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <p className="text-xs text-ink/60 max-w-md leading-relaxed">
              By submitting, you confirm that all information provided is accurate
              and that you have authority to request records for this student.
            </p>
            <button
              type="submit"
              disabled={submitting}
              className="bg-purple text-white hover:bg-purple-dark font-bold px-8 py-3 rounded-sm text-sm inline-flex items-center gap-2 disabled:opacity-60"
              data-testid="submit-records-request"
            >
              {submitting && <Loader2 size={16} className="animate-spin" />}
              {submitting ? "Submitting..." : "Submit request"}
              {!submitting && <ArrowRight size={16} />}
            </button>
          </div>
        </form>
      </section>

      <SiteFooter />
    </div>
  );
}

function SectionCard({ title, children }) {
  return (
    <div>
      <div className="text-xs tracking-[0.15em] uppercase text-purple-dark font-bold mb-4">{title}</div>
      <div className="border border-grey bg-white p-6">{children}</div>
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <div>
      <Label className="text-xs tracking-[0.15em] uppercase text-purple-dark font-bold mb-2 block">
        {label}{required && <span className="text-red-500 ml-1">*</span>}
      </Label>
      {children}
    </div>
  );
}

function PayOption({ id, label, desc, checked }) {
  return (
    <label
      htmlFor={id}
      className={`flex items-start gap-3 border p-4 cursor-pointer transition-colors ${
        checked ? "border-purple bg-grey-light" : "border-grey hover:border-purple-light bg-white"
      }`}
    >
      <RadioGroupItem value={id} id={id} className="mt-1" />
      <div>
        <div className="font-bold text-purple-dark text-sm">{label}</div>
        <div className="text-xs text-ink/70 mt-1">{desc}</div>
      </div>
    </label>
  );
}
